import { mPerLat, mPerLon } from './geo';
import { offsetM } from './excavation';
export { offsetM };

// local metric frame around a reference point
const kx = (lat) => mPerLon(lat);
const toXY = (o, p) => offsetM(o, p);
const toLL = (o, [x, y]) => [o[0] + y / mPerLat(o[0]), o[1] + x / kx(o[0])];

// arc intersection (Bogenschlag): circles around A (da) and B (db); picks solution nearest to `near`
export function arcPoint(a, b, da, db, near) {
  const [bx, by] = toXY(a, b), d = Math.hypot(bx, by);
  if (!d || d > da + db || d < Math.abs(da - db)) return null;
  const l = (da * da - db * db + d * d) / (2 * d), h = Math.sqrt(Math.max(0, da * da - l * l));
  const ux = bx / d, uy = by / d, mx = ux * l, my = uy * l;
  const c = [[mx - uy * h, my + ux * h], [mx + uy * h, my - ux * h]].map((p) => toLL(a, p));
  const [nx, ny] = toXY(a, near);
  return c.sort((p, q) => Math.hypot(...toXY(a, p).map((v, i) => v - [nx, ny][i])) - Math.hypot(...toXY(a, q).map((v, i) => v - [nx, ny][i])))[0];
}

// arc with fallback: no intersection -> place proportionally A:B on line A–B (approx)
export function arcFit(a, b, da, db, near) {
  const exact = arcPoint(a, b, da, db, near);
  if (exact) return { pt: exact, approx: false };
  const [bx, by] = toXY(a, b), d = Math.hypot(bx, by);
  if (!d || !(da + db > 0)) return { pt: null };
  const l = d > da + db ? (d * da) / (da + db) : da >= db ? da : -da;
  return { pt: toLL(a, [(bx / d) * l, (by / d) * l]), approx: true };
}

// orthogonal: `along` from A towards B, then `offset` perpendicular (side L/R of A→B)
export function orthoPoint(a, b, along, offset, side) {
  const [bx, by] = toXY(a, b), d = Math.hypot(bx, by);
  if (!d) return null;
  const ux = bx / d, uy = by / d, s = side === 'L' ? 1 : -1;
  return toLL(a, [ux * along - uy * offset * s, uy * along + ux * offset * s]);
}

// single fixed point: distance from A in direction of the current position
export function rayPoint(a, near, dist) {
  const [x, y] = toXY(a, near), d = Math.hypot(x, y);
  return d ? toLL(a, [(x / d) * dist, (y / d) * dist]) : null;
}

export const distM = (p, q) => Math.hypot(...toXY(p, q));

// dashed dimension lines of a survey (for map + PDF)
export function surveyDims(s, pt) {
  if (!s || !pt) return [];
  const fmt = (v) => `${(+v).toFixed(2).replace('.', ',')} m`;
  if (s.method === 'ortho') {
    const foot = orthoPoint(s.a, s.b, s.along, 0, s.side);
    return [{ p: s.a, q: s.b, label: '', base: true }, { p: s.a, q: foot, label: fmt(s.along) }, { p: foot, q: pt, label: fmt(s.offset) }];
  }
  return [{ p: s.a, q: pt, label: fmt(s.da) }, ...(s.b && s.method === 'arc' ? [{ p: s.b, q: pt, label: fmt(s.db) }] : [])];
}

// all surveys in a project: fittings + pipe vertices
export function allSurveys(project) {
  const out = [];
  (project.fittings || []).forEach((f) => f.survey && out.push({ s: f.survey, pt: [f.lat, f.lon], name: f.name }));
  (project.pipes || []).forEach((p) => Object.entries(p.surveys || {}).forEach(([i, s]) => Array.isArray(p.points[i]) && out.push({ s, pt: p.points[i], name: `${p.name} – P${+i + 1}` })));
  return out;
}

// survey summary text
export const surveyText = (s, t) => !s ? '' : s.method === 'ortho'
  ? `${t.svyTitle}: ${s.labelA || 'A'}→${s.labelB || 'B'} ${t.svyAlong} ${(+s.along).toFixed(2)} m · ${t.svyOffset} ${(+s.offset).toFixed(2)} m (${s.side})`
  : `${t.svyTitle}: ${s.labelA || 'A'} ${(+s.da).toFixed(2)} m${s.b && s.method === 'arc' ? ` · ${s.labelB || 'B'} ${(+s.db).toFixed(2)} m` : ''}`;

export const svyRefLabel = (k, t) => (k === 'gg' ? t.fit_gg : t.svyRefHouse);
export const fmtM = (v) => `${(+v).toFixed(2).replace('.', ',')} m`;
// display label of the surveyed object (e.g. "SS (Schieber 3)")
export const svyTargetLabel = (t, tg, project) => {
  if (tg.kind === 'vertex') return `${t.svyVertexLbl} P${tg.i + 1}`;
  const f = project.fittings.find((x) => x.id === tg.id);
  return f ? `${f.sym || t['fab_' + f.type] || ''} (${f.name})`.trim() : '';
};
export const isBendTarget = (tg, project) => tg.kind === 'vertex' || project.fittings.find((x) => x.id === tg.id)?.sym === 'BO';
